import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Code2,
  Megaphone,
  ArrowLeft,
  FileText,
  Share2,
  Github,
  Loader2,
  AlertCircle,
  FolderOpen,
  X,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useWorldStore } from "@/stores/worldStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { isTauri } from "@/hooks/usePlatform";
import { useToast } from "@/hooks/use-toast";
import type { Project } from "@/stores/worldStore";

function FormError({ message }: { message: string }) {
  return (
    <div role="alert" className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-md">
      <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

export function ProjectCategoryIcon({
  category,
  size = "sm",
  className,
}: {
  category: Project["project_category"];
  size?: "sm" | "lg";
  className?: string;
}) {
  const dims = size === "lg" ? "w-12 h-12" : "w-5 h-5";
  const iconDims = size === "lg" ? "w-6 h-6" : "w-3 h-3";
  if (category === "marketing") {
    return (
      <div className={cn(`${dims} rounded bg-purple-500/10 flex items-center justify-center`, size === "lg" && "rounded-lg", className)}>
        <Megaphone className={cn(iconDims, "text-purple-500")} />
      </div>
    );
  }
  return (
    <div className={cn(`${dims} rounded bg-blue-500/10 flex items-center justify-center`, size === "lg" && "rounded-lg", className)}>
      <Code2 className={cn(iconDims, "text-blue-500")} />
    </div>
  );
}

type CreationStep =
  | { step: "category" }
  | { step: "coding_setup" }
  | { step: "marketing_subtype" }
  | { step: "content_setup" };

interface ProjectCreationFlowProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProjectCreationFlow({
  isOpen,
  onClose,
}: ProjectCreationFlowProps) {
  const [current, setCurrent] = useState<CreationStep>({ step: "category" });

  const handleClose = () => {
    setCurrent({ step: "category" });
    onClose();
  };

  const handleBack = () => {
    switch (current.step) {
      case "coding_setup":
      case "marketing_subtype":
        setCurrent({ step: "category" });
        break;
      case "content_setup":
        setCurrent({ step: "marketing_subtype" });
        break;
    }
  };

  const showBack = current.step !== "category";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="sm:max-w-[500px]"
        data-testid="modal-project-creation-flow"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            {showBack && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleBack}
                aria-label="Go back"
                data-testid="button-creation-back"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div>
              <DialogTitle>
                {current.step === "category" && "New Project"}
                {current.step === "coding_setup" && "New Coding Project"}
                {current.step === "marketing_subtype" && "Marketing Project"}
                {current.step === "content_setup" && "Content Project Setup"}
              </DialogTitle>
              <DialogDescription>
                {current.step === "category" &&
                  "What type of project are you creating?"}
                {current.step === "coding_setup" &&
                  "Set up your coding project"}
                {current.step === "marketing_subtype" &&
                  "Choose a marketing project type"}
                {current.step === "content_setup" &&
                  "Configure your content project"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {current.step === "category" && (
          <CategorySelector
            onSelectCoding={() => setCurrent({ step: "coding_setup" })}
            onSelectMarketing={() =>
              setCurrent({ step: "marketing_subtype" })
            }
          />
        )}
        {current.step === "coding_setup" && (
          <CodingSetupStep onClose={handleClose} />
        )}
        {current.step === "marketing_subtype" && (
          <SubtypeSelector
            onSelectContent={() => setCurrent({ step: "content_setup" })}
          />
        )}
        {current.step === "content_setup" && (
          <ContentProjectSetup onClose={handleClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CategorySelector({
  onSelectCoding,
  onSelectMarketing,
}: {
  onSelectCoding: () => void;
  onSelectMarketing: () => void;
}) {
  return (
    <div
      className="grid grid-cols-2 gap-4 pt-4"
      data-testid="category-selector"
    >
      <button
        onClick={onSelectCoding}
        className={cn(
          "flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-border",
          "hover:border-blue-500 hover:bg-blue-500/5 transition-colors cursor-pointer text-center"
        )}
        data-testid="button-category-coding"
      >
        <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
          <Code2 className="w-6 h-6 text-blue-500" />
        </div>
        <div>
          <div className="font-medium">Coding</div>
          <div className="text-xs text-muted-foreground mt-1">
            Software projects with GitHub, tasks & AI agents
          </div>
        </div>
      </button>

      <button
        onClick={onSelectMarketing}
        className={cn(
          "flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-border",
          "hover:border-purple-500 hover:bg-purple-500/5 transition-colors cursor-pointer text-center"
        )}
        data-testid="button-category-marketing"
      >
        <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
          <Megaphone className="w-6 h-6 text-purple-500" />
        </div>
        <div>
          <div className="font-medium">Marketing</div>
          <div className="text-xs text-muted-foreground mt-1">
            Content, SEO & social media with AI workflows
          </div>
        </div>
      </button>
    </div>
  );
}

function CodingSetupStep({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [githubRepo, setGithubRepo] = useState("");
  const [subdirectory, setSubdirectory] = useState("");
  const [zoneId, setZoneId] = useState<string>("");
  const [description, setDescription] = useState("");
  const [localPath, setLocalPath] = useState<string | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { zones, addProject, saveError, clearSaveError } = useWorldStore();
  const { toast } = useToast();
  const { getEffectiveWorkspaceId, getActiveWorkspaceType } =
    useWorkspaceStore();
  const isLocalWorkspace = isTauri() && getActiveWorkspaceType() === "local";

  const handleOpenFolder = async () => {
    setError(null);
    setIsDetecting(true);
    try {
      const { open } = await import("@tauri-apps/plugin-dialog");
      const selected = await open({ directory: true, multiple: false });
      if (!selected) {
        setIsDetecting(false);
        return;
      }
      const folderPath =
        typeof selected === "string" ? selected : selected[0];
      if (!folderPath) {
        setIsDetecting(false);
        return;
      }
      setLocalPath(folderPath);
      const { detectProject } = await import("@/lib/project-detect");
      const detected = await detectProject(folderPath);
      if (!name.trim()) setName(detected.name);
      if (detected.githubRepo) setGithubRepo(detected.githubRepo);
      if (detected.description) setDescription(detected.description);
    } catch (e) {
      console.error("Folder detection failed:", e);
      setError("Failed to read folder. Please try again.");
    } finally {
      setIsDetecting(false);
    }
  };

  const normalizeSubdirectory = (value: string) =>
    value
      .trim()
      .replace(/^[/\\]+/, "")
      .replace(/[/\\]+$/, "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsLoading(true);
    setError(null);
    clearSaveError();

    const effectiveId = getEffectiveWorkspaceId();
    if (!effectiveId) {
      setError("Select or create a workspace first.");
      setIsLoading(false);
      return;
    }

    const normalizedSubdirectory = normalizeSubdirectory(subdirectory);
    const normalizedRepo = githubRepo.trim();

    const result = await addProject(
      {
        name: name.trim(),
        status: "healthy",
        x: Math.floor(Math.random() * 8),
        y: Math.floor(Math.random() * 8),
        zoneId: zoneId ? parseInt(zoneId) : undefined,
        githubRepo: normalizedRepo || undefined,
        description: description.trim() || `New project: ${name.trim()}`,
        localPath: localPath || undefined,
        subdirectory: normalizedSubdirectory || undefined,
        monorepoGroup:
          normalizedSubdirectory && normalizedRepo
            ? normalizedRepo
            : undefined,
        project_category: "coding",
        project_subtype: null,
      },
      effectiveId
    );

    setIsLoading(false);
    if (result) {
      toast({
        title: "Project created",
        description: `${name.trim()} has been added`,
      });
      onClose();
    } else {
      // Read saveError from current store state (not stale closure)
      const currentSaveError = useWorldStore.getState().saveError;
      setError(currentSaveError || "Failed to create project");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-2">
      {isLocalWorkspace && (
        <div className="space-y-2">
          {localPath ? (
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md text-sm">
              <FolderOpen className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate flex-1" title={localPath}>
                {localPath}
              </span>
              <button
                type="button"
                onClick={() => {
                  setLocalPath(null);
                  setDescription("");
                }}
                aria-label="Remove selected folder"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleOpenFolder}
              disabled={isDetecting}
              data-testid="button-open-folder"
            >
              {isDetecting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FolderOpen className="w-4 h-4 mr-2" />
              )}
              {isDetecting ? "Detecting..." : "Open Folder"}
            </Button>
          )}
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="coding-name">Project Name</Label>
        <Input
          id="coding-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="My Microservice"
          required
          data-testid="input-project-name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="coding-repo">GitHub Repository (optional)</Label>
        <div className="relative">
          <Github className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="coding-repo"
            value={githubRepo}
            onChange={(e) => setGithubRepo(e.target.value)}
            placeholder="org/repo-name"
            className="pl-10"
            data-testid="input-github-repo"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="coding-subdir">Subdirectory (optional)</Label>
        <Input
          id="coding-subdir"
          value={subdirectory}
          onChange={(e) => setSubdirectory(e.target.value)}
          placeholder="services/api"
          data-testid="input-project-subdirectory"
        />
        <p className="text-xs text-muted-foreground px-1">
          For a single service inside a shared repository.
        </p>
      </div>
      {description && (
        <div className="text-sm text-muted-foreground px-1">{description}</div>
      )}
      <div className="space-y-2">
        <Label htmlFor="coding-zone">Zone (optional)</Label>
        <Select value={zoneId} onValueChange={setZoneId}>
          <SelectTrigger data-testid="select-zone">
            <SelectValue placeholder="Select a zone" />
          </SelectTrigger>
          <SelectContent>
            {zones.map((zone) => (
              <SelectItem key={zone.id} value={zone.id.toString()}>
                {zone.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error && <FormError message={error} />}
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading || !name.trim()}
          data-testid="button-submit-add-project"
        >
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Create Project
        </Button>
      </div>
    </form>
  );
}

function SubtypeSelector({
  onSelectContent,
}: {
  onSelectContent: () => void;
}) {
  return (
    <div className="space-y-3 pt-4" data-testid="subtype-selector">
      <button
        onClick={onSelectContent}
        className={cn(
          "w-full flex items-start gap-4 p-4 rounded-lg border-2 border-border",
          "hover:border-purple-500 hover:bg-purple-500/5 transition-colors cursor-pointer text-left"
        )}
        data-testid="button-subtype-content"
      >
        <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5 text-purple-500" />
        </div>
        <div>
          <div className="font-medium">Content</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            SEO/GEO blog posts, glossary pages, comparison articles
          </div>
        </div>
      </button>

      <button
        disabled
        className={cn(
          "w-full flex items-start gap-4 p-4 rounded-lg border-2 border-border",
          "opacity-50 cursor-not-allowed text-left"
        )}
        data-testid="button-subtype-social"
      >
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
          <Share2 className="w-5 h-5 text-muted-foreground" />
        </div>
        <div>
          <div className="font-medium text-muted-foreground">Social</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Social media scheduling & automation — coming soon
          </div>
        </div>
      </button>
    </div>
  );
}

function ContentProjectSetup({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [blogUrl, setBlogUrl] = useState("");
  const [description, setDescription] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { addProject, saveError, clearSaveError } = useWorldStore();
  const { toast } = useToast();
  const { getEffectiveWorkspaceId } = useWorkspaceStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsLoading(true);
    setError(null);
    clearSaveError();

    const effectiveId = getEffectiveWorkspaceId();
    if (!effectiveId) {
      setError("Select or create a workspace first.");
      setIsLoading(false);
      return;
    }

    const result = await addProject(
      {
        name: name.trim(),
        status: "healthy",
        x: Math.floor(Math.random() * 8),
        y: Math.floor(Math.random() * 8),
        description:
          description.trim() || `Content project: ${name.trim()}`,
        project_category: "marketing",
        project_subtype: "content",
        projectConfig: {
          domain: domain.trim() || undefined,
          blogUrl: blogUrl.trim() || undefined,
        },
      },
      effectiveId
    );

    setIsLoading(false);
    if (result) {
      toast({
        title: "Content project created",
        description: `${name.trim()} is ready`,
      });
      onClose();
    } else {
      const currentSaveError = useWorldStore.getState().saveError;
      setError(currentSaveError || "Failed to create project");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 pt-2"
      data-testid="content-project-setup"
    >
      <div className="space-y-2">
        <Label htmlFor="content-name">Project Name</Label>
        <Input
          id="content-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="My Blog"
          required
          data-testid="input-content-name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="content-domain">Domain</Label>
        <Input
          id="content-domain"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="example.com"
          data-testid="input-content-domain"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="content-blog-url">Blog URL</Label>
        <Input
          id="content-blog-url"
          value={blogUrl}
          onChange={(e) => setBlogUrl(e.target.value)}
          placeholder="https://example.com/blog"
          data-testid="input-content-blog-url"
        />
      </div>

      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-muted-foreground w-full justify-start"
            data-testid="button-advanced-toggle"
          >
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform",
                showAdvanced && "rotate-180"
              )}
            />
            Advanced
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-3 pt-2">
          <div className="space-y-2">
            <Label htmlFor="content-description">Description</Label>
            <Input
              id="content-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional project description"
              data-testid="input-content-description"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {error && <FormError message={error} />}
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading || !name.trim()}
          data-testid="button-submit-content-project"
        >
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Create Content Project
        </Button>
      </div>
    </form>
  );
}
